<template>
  <div
    ref="panelRef"
    class="task-paths-map-panel"
  >
    <map-base
      ref="mapBaseRef"
      :show-geo-search-control="false"
      class="task-paths-map-panel__map"
    />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import L from 'leaflet'
import MapBase from '@/global/components/map/MapBase.vue'
import { isArray, isFunction } from '@/global/utils/utils'
import {
  createStreetLayer,
  groupStreetsByTask,
  streetsSignature
} from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/useStreetShapes'

const props = defineProps({
  // The streets to draw, as the backend reports them. Whoever owns the list decides
  // what is worth looking at - this only paints it.
  streets: {
    type: Array,
    default: () => ([])
  },
  // Whether the routes carry the arrows that say which way they are driven. Worth it
  // while one task is on the map, off where a whole page of them is.
  showArrows: {
    type: Boolean,
    default: true
  },
  // DataTable hands a side-table component its `config`; the table composable puts the
  // streets on it. Read as a fallback so the same panel serves the listing's side map
  // and the details screens, which pass `streets` directly.
  config: {
    type: Object,
    default: () => ({})
  }
})

const mapBaseRef = ref(null)
const panelRef = ref(null)
let isMapReady = false
let resizeObserver = null
// A draw asked for while the panel was laid out at no size - a tab that is not the
// open one - waiting for it to be shown.
let isRenderPending = false
// What is drawn for each task, keyed the way the streets report their task: its layers
// and a signature of the streets they were built from. Ticking a task off is then a
// matter of adding or dropping that task's own layers rather than laying the whole
// selection out again, and a task whose streets changed under it - a colour saved in
// the form beside the map - is the only one rebuilt.
const drawnByTask = new Map()
// The group all of them sit in. Added to the map once and never taken off, so the
// canvas below stays attached and keeps its context.
let streetsGroup = null
let canvasRenderer = null

/**
 * Leaflet draws each route as its own SVG element by default, which is what turns a
 * few thousand of them into a frozen page. One canvas paints the lot instead - kept
 * for as long as the group it draws for stays on the map.
 */
function getCanvasRenderer () {
  if (!canvasRenderer) {
    canvasRenderer = L.canvas({ padding: 0.5 })
  }

  return canvasRenderer
}

const showArrows = computed(() => props.showArrows !== false && props.config?.showArrows !== false)

const streets = computed(() => {
  if (isArray(props.streets) && props.streets.length) {
    return props.streets
  }

  // The listing puts a function here rather than a list, so that a changed selection
  // does not have the table's whole config rebuilt around it - see `getSelectedStreets`
  // in the listing's `useTable`. Called here, which is what makes this panel, and
  // nothing else, follow it. The details screens pass a plain list, as before.
  const fromConfig = props.config?.streets
  const resolved = isFunction(fromConfig) ? fromConfig() : fromConfig

  return isArray(resolved) ? resolved : []
})

function getStreetsGroup () {
  const map = mapBaseRef.value?.getMapObject()

  if (!map) {
    return null
  }

  if (!streetsGroup) {
    streetsGroup = L.featureGroup().addTo(map)
  }

  return streetsGroup
}

/**
 * Brings the map in line with the streets it was given, touching only what changed:
 * tasks that are no longer picked have their layers dropped, newly picked ones are
 * laid out, and everything already drawn is left exactly as it is. Ticking one task
 * off a selection of two hundred then costs one task's worth of work rather than the
 * whole selection's.
 */
function drawStreets () {
  const group = getStreetsGroup()

  if (!group) {
    return
  }

  const streetsByTask = groupStreetsByTask(streets.value)
  let changed = false

  const dropTask = (taskKey) => {
    group.removeLayer(drawnByTask.get(taskKey).layers)
    drawnByTask.delete(taskKey)
    changed = true
  }

  drawnByTask.forEach((drawn, taskKey) => {
    const taskStreets = streetsByTask.get(taskKey)

    // Gone from the selection, or no longer the streets these layers were built from.
    if (!taskStreets || streetsSignature(taskStreets) !== drawn.signature) {
      dropTask(taskKey)
    }
  })

  streetsByTask.forEach((taskStreets, taskKey) => {
    if (drawnByTask.has(taskKey)) {
      return
    }

    const layers = L.featureGroup()

    taskStreets.forEach(street => {
      const layer = createStreetLayer(street, {
        renderer: getCanvasRenderer(),
        tooltipClass: 'task-paths-map-tooltip',
        withArrows: showArrows.value
      })

      if (layer) {
        layers.addLayer(layer)
      }
    })

    drawnByTask.set(taskKey, {
      layers,
      signature: streetsSignature(taskStreets)
    })
    group.addLayer(layers)
    changed = true
  })

  if (!changed) {
    return
  }

  const bounds = group.getBounds()

  if (bounds?.isValid()) {
    mapBaseRef.value?.getMapObject()?.fitBounds(bounds, { padding: [50, 50] })
  }
}

function render () {
  if (!isMapReady || !mapBaseRef.value) {
    return
  }

  // Fitting the map to the route needs a container to fit it into. Asked while the
  // panel has none - a tab that is not the open one is still mounted, only at no size
  // - leaflet answers with the whole world, which is the zoom the tab is then opened
  // at. So the draw waits for it to be shown.
  if (!panelRef.value?.offsetWidth || !panelRef.value?.offsetHeight) {
    isRenderPending = true

    return
  }

  isRenderPending = false

  drawStreets()
}

onMounted(() => {
  // Registering through `onMapReady` rather than the `ready` event skips MapBase's own
  // default company/Zagreb re-centering, which would otherwise run right after and
  // override the fitBounds the draw does.
  mapBaseRef.value?.onMapReady(() => {
    isMapReady = true
    render()
  })

  // The panel is laid out against whatever sits next to it, so its height moves with
  // that. Leaflet only learns about a resized container when it is told, and until it
  // is it keeps painting tiles for the size it was first laid out at.
  if (typeof ResizeObserver === 'undefined' || !panelRef.value) {
    return
  }

  resizeObserver = new ResizeObserver(() => {
    mapBaseRef.value?.invalidateSize?.()

    // The panel has just been shown, and the route it was asked to draw while it had
    // nowhere to draw it can finally be fitted.
    if (isRenderPending) {
      render()
    }
  })
  resizeObserver.observe(panelRef.value)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
  // Anything still waiting for its turn to draw has nothing left to draw onto.
  isMapReady = false
  drawnByTask.clear()
  streetsGroup = null
  canvasRenderer = null
})

// Not deep: the list is replaced whole on every change, and walking a few thousand
// streets on each one costs as much as the drawing does.
watch(streets, render)
</script>

<script>
export default {
  name: 'TaskPathsMapPanel',
  // Used as a side-table component too, and DataTable hands that slot a handful of
  // props this panel has no use for - they must not land as attributes on the root.
  inheritAttrs: false
}
</script>

<style scoped>
.task-paths-map-panel {
  display: flex;
  /* The loader overlays the panel rather than the page, so it needs something to be
     positioned against. */
  position: relative;
  height: 100%;
  width: 100%;
  min-height: 320px;
  border-radius: 16px;
  overflow: hidden;
  /* Leaflet's own controls/panes reach z-index 1000-9999 internally. Without its own
     stacking context that competes with page-level chrome like the sticky app header
     once the page scrolls. */
  isolation: isolate;
}

.task-paths-map-panel__map {
  flex: 1 1 auto;
}
</style>

<style lang="scss">
/* Leaflet tooltips default to `white-space: nowrap`, so a long street name grew the
   tooltip wider than the panel itself and got clipped at its edge. */
.leaflet-tooltip.task-paths-map-tooltip {
  width: max-content;
  max-width: 240px;
  white-space: normal;
  overflow-wrap: break-word;
  text-align: center;
}
</style>
